import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OEEGauge } from "./OEEGauge.view";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { percentageChecker } from "utilities/utilities";

export enum OEESidebarView {
    OEE = "oee",
    AVAILABILITY = "availability",
    PERFORMANCE = "performance",
    QUALITY = "quality",
}

interface OEEGaugesSidebarProps {
    selectedView: OEESidebarView;
    onSelectView: (view: OEESidebarView) => void;
}

export const OEEGaugesSidebar = ({ selectedView, onSelectView }: OEEGaugesSidebarProps) => {
    const { t } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const { shift } = useContext(HMIContext);

    const selectedShiftTimes =
        shift === ShiftOptionsEnum.current
            ? store.lastTwoShiftsEventReports?.currentShiftTimes
            : store.lastTwoShiftsEventReports?.previousShiftTimes;

    const selectedOEESummary =
        shift === ShiftOptionsEnum.current
            ? store.lastTwoShiftsEventReports?.currentShiftPerformanceSummary
            : store.lastTwoShiftsEventReports?.previousShiftPerformanceSummary;

    const selectedQualitySummary =
        shift === ShiftOptionsEnum.current
            ? store.lastTwoShiftsEventReports?.currentShiftQualitySummary
            : store.lastTwoShiftsEventReports?.previousShiftQualitySummary;

    const availabilityPercentage = useMemo(() => {
        if (!selectedShiftTimes) return null;
        if (selectedShiftTimes.downtime === 0) return 100;
        return percentageChecker(
            Math.floor((100 * (selectedShiftTimes.shift - selectedShiftTimes.downtime)) / selectedShiftTimes.shift),
        );
    }, [selectedShiftTimes]);

    const performancePercentage = useMemo(() => {
        if (!selectedOEESummary || selectedOEESummary.length === 0) return null;
        let totalWeightedRatio = 0;
        let totalRunTime = 0;
        for (const r of selectedOEESummary) {
            if (r.performance_ratio !== null && r.run_time_seconds > 0) {
                totalWeightedRatio += r.performance_ratio * r.run_time_seconds;
                totalRunTime += r.run_time_seconds;
            }
        }
        if (totalRunTime === 0) return null;
        return Math.round((totalWeightedRatio / totalRunTime) * 100);
    }, [selectedOEESummary]);

    const qualityPercentage = useMemo(() => {
        if (!selectedQualitySummary || selectedQualitySummary.length === 0) return null;
        let totalGood = 0;
        let totalPieces = 0;
        for (const r of selectedQualitySummary) {
            if (r.quality_ratio !== null && r.total_count > 0) {
                totalGood += r.good_count;
                totalPieces += r.total_count;
            }
        }
        if (totalPieces === 0) return null;
        return Math.round((totalGood / totalPieces) * 100);
    }, [selectedQualitySummary]);

    const qualityEnabled = useMemo(
        () => !!store?.machineCenter?.machine_center_oee_config?.record_quality_enabled,
        [store],
    );

    const GAUGE_KEYS: { key: OEESidebarView; translationKey: string }[] = [
        { key: OEESidebarView.OEE, translationKey: "HMI.oee_sidebar.oee" },
        { key: OEESidebarView.AVAILABILITY, translationKey: "HMI.oee_sidebar.availability" },
        { key: OEESidebarView.PERFORMANCE, translationKey: "HMI.oee_sidebar.performance" },
        ...(qualityEnabled ? [{ key: OEESidebarView.QUALITY, translationKey: "HMI.oee_sidebar.quality" }] : []),
    ];

    const gaugePercentages = useMemo<Record<OEESidebarView, number | undefined | null>>(
        () => ({
            [OEESidebarView.OEE]: undefined,
            [OEESidebarView.AVAILABILITY]: availabilityPercentage,
            [OEESidebarView.PERFORMANCE]: performancePercentage,
            [OEESidebarView.QUALITY]: qualityPercentage,
        }),
        [availabilityPercentage, performancePercentage, qualityPercentage],
    );

    return (
        <div
            style={{
                width: 190,
                minWidth: 190,
                backgroundColor: "#F5FAFF",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "12px 6px",
                boxShadow: "4px 0px 0px rgba(62, 93, 138, 0.2)",
                position: "relative",
                zIndex: 1,
            }}
        >
            {GAUGE_KEYS.map(({ key, translationKey }) => {
                const isSelected = selectedView === key;
                return (
                    <div
                        key={key}
                        onClick={() => onSelectView(key)}
                        style={{
                            cursor: "pointer",
                            borderRadius: 6,
                            borderLeft: isSelected ? "4px solid #3E5D8A" : "4px solid transparent",
                            backgroundColor: isSelected ? "#FFFFFF" : "transparent",
                            boxShadow: isSelected ? "0 2px 8px rgba(62, 93, 138, 0.15)" : "none",
                            transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                            padding: "4px 2px",
                        }}
                    >
                        <OEEGauge label={t(translationKey)} percentage={gaugePercentages[key]} />
                    </div>
                );
            })}
        </div>
    );
};
